import * as StoreApi from "../../api/storeApi";
import * as StoreInventoryApi from "../../api/storeInventoryApi";
import {
  AwaitingStoreOrderProposal,
  RemoveOrder,
  Store,
  StoreOrder,
  StoreOrderState
} from "../../models/Store";

import * as StoreDistributorApi from "../../api/storeDistributorApi";
import * as StoreOrderApi from "../../api/storeOrderApi";
import {
  REMOVE_STORE_ORDERS,
  REQUEST_ALL_STORE_INVENTORY_ITEMS,
  REQUEST_AWAITING_STORE_ORDER_PROPOSALS,
  REQUEST_FILTERED_STORES,
  REQUEST_FILTERED_STORES_DONE,
  REQUEST_STORE_DISTRIBUTORS,
  REQUEST_STORE_DISTRIBUTORS_DONE,
  REQUEST_STORE_INVENTORY_ITEMS,
  REQUEST_STORE_INVENTORY_ITEMS_DONE,
  REQUEST_STORE_ORDERS,
  REQUEST_STORE_ORDERS_DONE,
  REQUEST_STORES,
  REQUEST_STORES_DONE,
  SAVE_FILTERED_STORES,
  SAVE_FILTERED_STORES_DONE,
  StoreActionTypes
} from "./types";
import { MinimalStoreInventoryItem } from "../../models/StoreInventory";
import { isOk } from "../../result";
import { StoreDistributor } from "../../models/StoreDistributor";
import { getFilteredStores, saveFilteredStoresToLocalStorage } from "./utils";

function fetchStores(): StoreActionTypes {
  return {
    type: REQUEST_STORES,
    payload: []
  };
}

function fetchStoresDone(stores: ReadonlyArray<Store>): StoreActionTypes {
  return {
    type: REQUEST_STORES_DONE,
    payload: stores
  };
}

function fetchStoreInventoryItems(storeId: string): StoreActionTypes {
  return {
    type: REQUEST_STORE_INVENTORY_ITEMS,
    storeId,
    payload: []
  };
}

function fetchStoreInventoryItemsDone(
  storeId: string,
  items: ReadonlyArray<MinimalStoreInventoryItem>
): StoreActionTypes {
  return {
    type: REQUEST_STORE_INVENTORY_ITEMS_DONE,
    storeId,
    payload: items
  };
}

function fetchAllStoreInventoryItems(
  items: ReadonlyArray<MinimalStoreInventoryItem>
): StoreActionTypes {
  return {
    type: REQUEST_ALL_STORE_INVENTORY_ITEMS,
    payload: items
  };
}

function fetchStoreDistributors(storeId: string): StoreActionTypes {
  return {
    type: REQUEST_STORE_DISTRIBUTORS,
    storeId,
    payload: []
  };
}

function fetchStoreDistributorsDone(
  storeId: string,
  items: ReadonlyArray<StoreDistributor>
): StoreActionTypes {
  return {
    type: REQUEST_STORE_DISTRIBUTORS_DONE,
    storeId,
    payload: items
  };
}

function fetchAwaitingStoreOrderProposals(
  items: ReadonlyArray<AwaitingStoreOrderProposal>
): StoreActionTypes {
  return {
    type: REQUEST_AWAITING_STORE_ORDER_PROPOSALS,
    payload: items
  };
}

function fetchStoreOrders(storeId: string | "all"): StoreActionTypes {
  return {
    type: REQUEST_STORE_ORDERS,
    storeId,
    payload: []
  };
}

function fetchStoreOrdersDone(
  storeId: string | "all",
  items: ReadonlyArray<StoreOrder>
): StoreActionTypes {
  return {
    type: REQUEST_STORE_ORDERS_DONE,
    storeId,
    payload: items
  };
}

function removeStoreOrders(orders: ReadonlyArray<RemoveOrder>) {
  return {
    type: REMOVE_STORE_ORDERS,
    payload: orders
  };
}

export const thunkFetchStores = () => async (dispatch: any) => {
  dispatch(fetchStores());
  const result = await StoreApi.list();

  if (!isOk(result)) {
    console.error(result.details);
    dispatch(fetchStoresDone([]));
    return;
  }

  dispatch(fetchStoresDone(result.value));
};

export const thunkFetchStoreInventoryItems =
  (storeId: string) => async (dispatch: any) => {
    dispatch(fetchStoreInventoryItems(storeId));
    const result = await StoreInventoryApi.list(storeId);

    if (!isOk(result)) {
      dispatch(fetchStoreInventoryItemsDone(storeId, []));
      console.error(result.details);
      return;
    }

    dispatch(fetchStoreInventoryItemsDone(storeId, result.value));
  };

export const thunkFetchAllStoreInventoryItems = () => async (dispatch: any) => {
  const result = await StoreInventoryApi.listAll();

  if (!isOk(result)) {
    console.error(result.details);
    return;
  }

  dispatch(fetchAllStoreInventoryItems(result.value));
};

export const thunkFetchStoreDistributors =
  (storeId: string) => async (dispatch: any) => {
    dispatch(fetchStoreDistributors(storeId));
    const result = await StoreDistributorApi.list(storeId);

    if (!isOk(result)) {
      dispatch(fetchStoreDistributorsDone(storeId, []));
      console.error(result.details);
      return;
    }

    dispatch(fetchStoreDistributorsDone(storeId, result.value));
  };

export const thunkFetchAwaitingStoreOrderProposals =
  () => async (dispatch: any) => {
    const result = await StoreOrderApi.listAwaitingStoreOrderProposals();

    if (!isOk(result)) {
      console.error(result.details);
      dispatch(fetchAwaitingStoreOrderProposals([]));
      return;
    }

    dispatch(fetchAwaitingStoreOrderProposals(result.value));
  };

export const thunkRemoveStoreOrders =
  (orders: ReadonlyArray<RemoveOrder>) => async (dispatch: any) => {
    const result = await StoreOrderApi.removeOrders(orders);

    if (isOk(result)) {
      dispatch(removeStoreOrders(orders));
      return;
    }

    console.error(result.details);
  };

export const thunkFetchStoreOrders =
  (
    storeId: string | "all",
    states: ReadonlyArray<StoreOrderState>,
    ignoreMerging: boolean = false
  ) =>
  async (dispatch: any) => {
    dispatch(fetchStoreOrders(storeId));

    const result = await StoreOrderApi.listOrders(
      storeId,
      states,
      ignoreMerging
    );

    if (!isOk(result)) {
      console.error(result.details);
      return;
    }

    dispatch(fetchStoreOrdersDone(storeId, result.value));
  };

/* Stores filter*/
function fetchFilteredStores(): StoreActionTypes {
  return {
    type: REQUEST_FILTERED_STORES,
    payload: []
  };
}

function fetchFilteredStoresDone(
  stores: ReadonlyArray<Store>
): StoreActionTypes {
  return {
    type: REQUEST_FILTERED_STORES_DONE,
    payload: stores
  };
}

function saveFilteredStores(): StoreActionTypes {
  return {
    type: SAVE_FILTERED_STORES,
    payload: []
  };
}

function saveFilteredStoresDone(stores: Store[]): StoreActionTypes {
  return {
    type: SAVE_FILTERED_STORES_DONE,
    payload: stores
  };
}

export const thunkFetchFilteredStores = () => async (dispatch: any) => {
  dispatch(fetchFilteredStores());
  let filteredStores: ReadonlyArray<Store> = [];
  try {
    const result = await StoreApi.list();
    if (isOk(result)) {
      filteredStores = getFilteredStores(result.value);
    } else {
      console.error(result.details);
    }
  } catch (e) {
    console.error("Error fetching filtered stores", e);
  }
  dispatch(fetchFilteredStoresDone(filteredStores));
};

export const thunkSaveFilteredStores =
  (stores: Store[]) => async (dispatch: any) => {
    try {
      dispatch(saveFilteredStores());
      const result = saveFilteredStoresToLocalStorage(stores);
      dispatch(saveFilteredStoresDone(result));
    } catch (e) {
      console.error(`Error saving stores to local storage`, e);
    }
  };
